import type { Ref } from 'react';
import { Check } from 'lucide-react';
import { PractitionerSearchList } from '@/components/appointments/PractitionerSearchList';
import { TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';

export type BlockStaffMember = {
  user_id: string;
  name: string;
};

/**
 * Behandler on Bloker tid: every clinic member, not only practitioner roles.
 * Hele klinikken stays pinned. An empty search lists the whole roster in the
 * scrollable list, selected people first. Typing narrows it and keeps
 * matching selections at the top. Selected people stay checked.
 */
export function BlockStaffPicker({
  members,
  selectedIds,
  onChange,
  disabled,
  error,
  legend,
  wholeClinicLabel,
  searchPlaceholder,
  emptyLabel,
  searchRef,
}: {
  members: readonly BlockStaffMember[];
  selectedIds: readonly string[];
  onChange: (ids: string[]) => void;
  disabled?: boolean;
  error?: string;
  legend: string;
  wholeClinicLabel: string;
  searchPlaceholder: string;
  emptyLabel: string;
  searchRef?: Ref<HTMLInputElement>;
}) {
  const allSelected =
    members.length > 0 && members.every((member) => selectedIds.includes(member.user_id));

  return (
    <fieldset className="staff-search space-y-s2" data-testid="block-staff-picker">
      <legend className="type-label text-ds-text">{legend}</legend>
      {members.length > 1 ? (
        <label className={cn('qp-r', allSelected && 'on', disabled && 'dis')}>
          <input
            id="block-whole-clinic"
            type="checkbox"
            className="sr-only"
            checked={allSelected}
            disabled={disabled}
            onChange={(event) => {
              onChange(event.target.checked ? members.map((member) => member.user_id) : []);
            }}
          />
          <span className="box" aria-hidden>
            {allSelected ? <Check className="h-3.5 w-3.5" strokeWidth={1.75} /> : null}
          </span>
          <span>
            <span className="nm">{wholeClinicLabel}</span>
          </span>
        </label>
      ) : null}
      <PractitionerSearchList
        embedded
        tone="tools"
        showEveryoneWhenIdle
        people={members.map((member) => ({ id: member.user_id, name: member.name }))}
        suggestedIds={[]}
        selectedIds={selectedIds}
        onChange={onChange}
        legend={legend}
        suggestionLabel=""
        searchPlaceholder={searchPlaceholder}
        emptyLabel={emptyLabel}
        disabled={disabled}
        inputRef={searchRef}
      />
      {error ? <p className={cn(TYPE.small, 'text-ds-danger')}>{error}</p> : null}
    </fieldset>
  );
}
